import { tooltipFactory } from '@milkdown/plugin-tooltip';
import { usePluginViewContext } from '@prosemirror-adapter/react';
import { useRef, useState, useEffect, useMemo, type KeyboardEvent } from 'react';
import { $ctx } from '@milkdown/kit/utils';
import {
  Button,
  CommandItem,
  CommandGroup,
  CommandInput,
  Command,
  CommandList,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@brainfish-ai/components';
import { ArrowSquareOut, Trash, File } from '@phosphor-icons/react';
import debounce from 'lodash/debounce';

import { LINK_TOOLTIP_NAME } from './constants';
import { applyLinkMark, removeLinkMark, requestTentativeLinkCleanup, type LinkInfo } from './link-utils';
import { useLinkTooltipState } from './use-tooltip-state';
import { useEditorAction } from '../../hooks/useEditorAction';
import type { SearchResult } from '../../types';

export interface LinkTooltipConfig {
  onSearchLink?: (term: string) => Promise<SearchResult[]>;
}

/**
 * Slice to store link tooltip configuration
 */
export const linkTooltipConfig = $ctx<LinkTooltipConfig, 'linkTooltipConfig'>({}, 'linkTooltipConfig');

/**
 * Create the tooltip plugin using Milkdown's tooltipFactory
 */
export const linkTooltip = tooltipFactory('LINK_TOOLTIP');

/**
 * React component for link tooltip
 * 
 * Uses queueMicrotask to defer state updates and editor transactions to avoid
 * "flushSync was called from inside a lifecycle method" warnings that occur
 * when ProseMirror state changes trigger React re-renders during lifecycle methods.
 */
export const LinkTooltipView = () => {
  const inputRef = useRef<HTMLInputElement>(null);
  const { view, prevState } = usePluginViewContext();
  const { action, getInstance } = useEditorAction();
  const ctx = getInstance()?.ctx;

  // Input and search state
  const [inputValue, setInputValue] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const searchRequestIdRef = useRef(0);

  // Tooltip state derived from editor selection
  const { tooltipState, dismissTooltip } = useLinkTooltipState(view, ctx, prevState);

  // Sync input when tooltip becomes visible or link changes
  // This fixes the bug where editing a link, pressing Escape, then returning
  // to the same link would show the stale edited value instead of actual href
  // Use queueMicrotask to defer state updates and avoid flushSync warnings
  const prevShowRef = useRef(false);
  const prevHref = useRef<string | null>(null);
  const tooltipJustBecameVisible = tooltipState.show && !prevShowRef.current;
  const hrefChanged = tooltipState.linkInfo?.href !== prevHref.current;

  if (tooltipJustBecameVisible || hrefChanged) {
    prevHref.current = tooltipState.linkInfo?.href ?? null;
    if (tooltipState.linkInfo) {
      // Defer state updates to avoid "flushSync was called from inside a lifecycle method" warning
      queueMicrotask(() => {
        setInputValue(tooltipState.linkInfo?.href || '');
        setSearchResults([]);
      });
    }
  }
  prevShowRef.current = tooltipState.show;

  // Reset input and search when tooltip (re)opens so we don't show stale search term/results
  // after clicking away and back (Platform: plugin view may not get a hide render, so sync
  // block alone can miss the transition).
  // Use queueMicrotask to defer state updates and avoid flushSync warnings
  const prevShowForResetRef = useRef(false);
  useEffect(() => {
    const justBecameVisible = tooltipState.show && !prevShowForResetRef.current;
    prevShowForResetRef.current = tooltipState.show;
    if (justBecameVisible && tooltipState.linkInfo) {
      // Defer state updates to avoid "flushSync was called from inside a lifecycle method" warning
      queueMicrotask(() => {
        setInputValue(tooltipState.linkInfo?.href || '');
        setSearchResults([]);
      });
    }
  }, [tooltipState.show, tooltipState.linkInfo]);

  // Focus input when tooltip becomes visible (after click events complete)
  useEffect(() => {
    if (tooltipState.show) {
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [tooltipState.show]);

  // When tooltip hides (Escape or click-away), request cleanup if the visible link was empty
  // Use queueMicrotask to defer editor state changes and avoid flushSync warnings
  const linkInfoWhileVisibleRef = useRef<LinkInfo | null>(null);
  if (tooltipState.show && tooltipState.linkInfo) {
    linkInfoWhileVisibleRef.current = tooltipState.linkInfo;
  }
  useEffect(() => {
    if (!tooltipState.show) {
      const hadEmptyLink = linkInfoWhileVisibleRef.current && !linkInfoWhileVisibleRef.current.href;
      linkInfoWhileVisibleRef.current = null;
      if (hadEmptyLink && ctx) {
        // Defer editor state change to avoid "flushSync was called from inside a lifecycle method" warning
        queueMicrotask(() => {
          requestTentativeLinkCleanup(ctx);
        });
      }
    }
  }, [tooltipState.show, ctx]);

  // Hide the default Crepe toolbar when our link tooltip is visible (avoid both showing at once)
  useEffect(() => {
    const editorContainer = view.dom.parentElement;
    if (!editorContainer) return;
    if (tooltipState.show) {
      editorContainer.setAttribute('data-link-tooltip-visible', 'true');
    } else {
      editorContainer.removeAttribute('data-link-tooltip-visible');
    }

    return () => editorContainer.removeAttribute('data-link-tooltip-visible');
  }, [view.dom.parentElement, tooltipState.show]);

  const config = action((ctx) => ctx.get(linkTooltipConfig.key)) ?? {};
  const { onSearchLink } = config;

  const debouncedSearch = useMemo(
    () =>
      debounce(async (value: string, searchFn?: LinkTooltipConfig['onSearchLink']) => {
        const trimmed = value.trim();

        if (trimmed && searchFn) {
          const requestId = ++searchRequestIdRef.current;
          try {
            const results = await searchFn(trimmed);
            if (requestId === searchRequestIdRef.current) {
              setSearchResults(results);
            }
          } catch {
            if (requestId === searchRequestIdRef.current) {
              setSearchResults([]);
            }
          }
        } else {
          setSearchResults([]);
        }
      }, 300),
    [],
  );

  // clean up - abort pending search when component unmounts
  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  const handleValueChange = (value: string) => {
    setInputValue(value);
    void debouncedSearch(value, onSearchLink);
  };

  const saveLink = (href: string, linkInfo?: LinkInfo | null) => {
    // Use passed linkInfo so we're not relying on tooltipState at click time — clicking
    // a result can blur/change selection before the handler runs, clearing linkInfo.
    const info = linkInfo ?? tooltipState.linkInfo;
    if (!info || href.trim().length === 0 || !ctx) return;
    // Defer editor state change to avoid flushSync warnings when called from event handlers
    queueMicrotask(() => {
      // Use ctx directly — action() no-ops when loading, so applyLinkMark never ran
      applyLinkMark(ctx, href.trim(), info.from, info.to);
      view.focus();
    });
  };

  const handleSelectResult = (result: SearchResult, linkInfo: LinkInfo | null) => saveLink(result.url, linkInfo);

  const handleRemoveLink = () => {
    if (!tooltipState.linkInfo) return;
    // Defer editor state change to avoid flushSync warnings when called from event handlers
    queueMicrotask(() => {
      action((ctx) => {
        removeLinkMark(ctx, tooltipState.linkInfo!.from, tooltipState.linkInfo!.to);
      });
      view.focus();
    });
  };

  const handleOpenLink = () => {
    if (tooltipState.linkInfo?.href) {
      window.open(tooltipState.linkInfo.href, '_blank', 'noopener,noreferrer');
    }
  };

  const hideTooltip = () => {
    dismissTooltip(tooltipState.linkInfo);
    view.focus();
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    switch (e.key) {
      case 'Enter':
        if (searchResults.length === 0) {
          e.preventDefault();
          saveLink(inputValue.trim());
        }
        break;
      case 'Escape':
        e.preventDefault();
        hideTooltip();
        break;
    }
  };

  if (!tooltipState.show) return null;

  const showResults = searchResults.length > 0;

  return (
    <div
      data-name={LINK_TOOLTIP_NAME}
      className="milkdown-toolbar absolute z-50 flex flex-col bg-surface border border-border rounded-lg shadow-lg min-w-[320px] max-w-[400px]"
      style={{
        top: `${tooltipState.position.top}px`,
        left: `${tooltipState.position.left}px`,
      }}
    >
      {/* turn off cmdk filtering and display what's in the searchResults array */}
      <Command shouldFilter={false}>
        <div className="flex items-center gap-1 pr-2! w-full [&>div]:flex-1">
          <CommandInput
            ref={inputRef}
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            value={inputValue}
            onValueChange={handleValueChange}
            onKeyDown={handleKeyDown}
            placeholder={config.onSearchLink ? 'Search or paste link...' : 'Paste link...'}
          />
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon" onClick={handleOpenLink} disabled={!tooltipState.linkInfo?.href}>
                <ArrowSquareOut size={16} />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Open link</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon" onClick={handleRemoveLink}>
                <Trash size={16} />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Remove link</TooltipContent>
          </Tooltip>
        </div>

        {showResults && (
          <CommandList>
            <CommandGroup>
              {searchResults.map((result) => {
                return (
                  <CommandItem
                    key={result.url}
                    value={result.url}
                    onSelect={() => handleSelectResult(result, tooltipState.linkInfo)}
                  >
                    <File size={16} className="shrink-0 opacity-60" />
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-medium">{result.title}</div>
                      {result.subtitle && <div className="truncate text-xs opacity-60">{result.subtitle}</div>}
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        )}
      </Command>
    </div>
  );
};
